import React, {useMemo} from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { CssBaseline, ThemeProvider, createTheme, Box, CircularProgress } from '@mui/material';

// Context
import { AuthProvider, useAuth } from './context/AuthContext';

// Components
import Home from './components/Home';
import Terms from './components/Terms';
import Login from './components/auth/Login';
import Dashboard from './components/dashboard/Dashboard';
import MembershipDetails from './components/dashboard/MembershipDetails';
import LocationsPage from './components/locations/LocationsPage';
import AccountPage from './components/account/AccountPage';
import ProtectedRoute from './components/auth/ProtectedRoute';
import AnonymousRoute from './components/auth/AnonymousRoute';
import EmailVerification from './components/auth/EmailVerification';
import PhoneVerification from './components/auth/PhoneVerification';
import ResetPassword from './components/auth/ResetPassword';
import GroupSignUp from './components/auth/GroupSignUp';
import MainLayout from './layouts/MainLayout';

// Layout wrapper component
const LayoutWrapper = () => {
  return (
    <MainLayout>
      <Outlet />
    </MainLayout>
  );
};

// Component that provides dynamic theming
function ThemedApp() {
  const { org, loading } = useAuth();

  // Create dynamic theme based on org colors
  const theme = useMemo(() => {
    const themeOptions = {
      palette: {
        primary: {
          main: (org && typeof org === 'object' && org.primary_color) ? org.primary_color : '#1976d2',
        },
        secondary: {
          main: (org && typeof org === 'object' && org.secondary_color) ? org.secondary_color : '#f50057',
        },
      },
    };

    return createTheme(themeOptions);
  }, [org]);

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          backgroundColor: '#f5f5f5'
        }}
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <Routes>
          {/* Route without MainLayout */}
          <Route path="/signup" element={<GroupSignUp />} />

          {/* Routes with MainLayout */}
          <Route element={<LayoutWrapper />}>
            <Route path="/" element={<Home />} />
            <Route path="/terms" element={<Terms />} />
            <Route
              path="/login"
              element={
                <AnonymousRoute>
                  <Login />
                </AnonymousRoute>
              }
            />
            <Route
              path="/register"
              element={
                <AnonymousRoute>
                  <Login />
                </AnonymousRoute>
              }
            />
            <Route
              path="/reset-password"
              element={
                <AnonymousRoute>
                  <ResetPassword />
                </AnonymousRoute>
              }
            />
            <Route
              path="/verify-phone"
              element={
                <ProtectedRoute>
                  <PhoneVerification />
                </ProtectedRoute>
              }
            />
            <Route
              path="/verify-email"
              element={
                <ProtectedRoute>
                  <EmailVerification />
                </ProtectedRoute>
              }
            />
            <Route
              path="/memberships"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/memberships/:companyId"
              element={
                <ProtectedRoute>
                  <MembershipDetails />
                </ProtectedRoute>
              }
            />
            <Route
              path="/locations"
              element={
                <ProtectedRoute>
                  <LocationsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/account"
              element={
                <ProtectedRoute>
                  <AccountPage />
                </ProtectedRoute>
              }
            />
          </Route>
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </Router>
    </ThemeProvider>
  );
}

function App() {
  return (
    <AuthProvider>
      <ThemedApp />
    </AuthProvider>
  );
}

export default App;